import { auth } from './auth.svelte.js';
import { billing, openPurchaseModal, openSubscriptionModal, manageSubscription, refreshBillingAccount, getCurrentSubscriptionCheckoutUrl } from './billing.svelte.js';
import { generationRun } from './generation-run.svelte.js';
import { getAccountSnapshot, onAccountUpdate } from '../sogni-client.svelte.js';
import { onBillingRefresh } from '../utils/billing-events.js';
import { getCreditRecoveryView } from '../utils/credit-recovery-policy.js';
import { choosePromptTokenRecovery, clearPromptTokenRecovery } from '../utils/prompt-billing-choice.js';

export const creditRecovery = $state({
  show: false, error: null, kind: 'images', request: null, quote: null, results: {},
  accountId: null, username: null, refreshing: false, resuming: false,
  awaitingBilling: false, refreshed: false, notice: null, actionError: null,
  billingMode: 'auto'
});

let activeResume = null;
let recoverySequence = 0;
let initialBilling = '';
const resumeHandlers = new Map();

export function installResumeHandler(kind, handler) {
  resumeHandlers.set(kind, handler);
  return () => { if (resumeHandlers.get(kind) === handler) resumeHandlers.delete(kind); };
}

function accountId(snapshot = getAccountSnapshot()) {
  return snapshot.walletAddress?.toLowerCase() || null;
}

function billingFingerprint(snapshot) {
  return JSON.stringify({ balance: snapshot.balance, subscription: snapshot.subscription });
}

function markRefreshed(id) {
  if (!creditRecovery.request || id !== creditRecovery.accountId || auth.username !== creditRecovery.username) return;
  creditRecovery.refreshed = true;
  creditRecovery.notice = 'Your account was refreshed. Review the saved request before continuing.';
  if (creditRecovery.awaitingBilling) {
    creditRecovery.awaitingBilling = false;
    creditRecovery.show = true;
  }
}

onBillingRefresh(markRefreshed);
onAccountUpdate((snapshot) => {
  clearPromptTokenRecovery(snapshot.authenticated ? accountId(snapshot) : null);
  if (!creditRecovery.accountId) return;
  if (!snapshot.authenticated || accountId(snapshot) !== creditRecovery.accountId) {
    dismissCreditRecovery();
    creditRecovery.request = null;
    activeResume = null;
    return;
  }
  if (billingFingerprint(snapshot) !== initialBilling) markRefreshed(accountId(snapshot));
});

/** Does not spend or retry. Review billing failures or an explicitly chosen
 * replacement for a saved retired/unavailable image model. */
export function recoverGenerationFailure(error, {
  kind = 'images', request = error?.request, quote = error?.quote,
  results, onResume
} = {}) {
  const snapshot = getAccountSnapshot();
  if (!snapshot.authenticated) return false;
  const view = getCreditRecoveryView(error, { subscription: snapshot.subscription, request, quote, kind });
  if (!view.recoverable) return false;
  if (!request && kind === 'images') request = generationRun.request;
  if (!quote && kind === 'images' && request?.id === generationRun.request?.id) quote = generationRun.quote;
  if (request?.accountId && request.accountId !== accountId(snapshot)) return false;
  recoverySequence += 1;
  initialBilling = billingFingerprint(snapshot);
  activeResume = onResume || null;
  Object.assign(creditRecovery, {
    show: true, error, kind, request: request || null, quote: quote || null,
    results: results || (kind === 'images' && request?.id === generationRun.request?.id ? generationRun.results : {}),
    accountId: accountId(snapshot), username: snapshot.username,
    refreshing: false, resuming: false, awaitingBilling: false, refreshed: false,
    notice: null, actionError: null, billingMode: 'auto'
  });
  return true;
}

export function getCurrentRecoveryView() {
  const results = creditRecovery.kind === 'images' && creditRecovery.request?.id === generationRun.request?.id
    ? generationRun.results : creditRecovery.results;
  return getCreditRecoveryView(creditRecovery.error, { subscription: auth.subscription,
    request: creditRecovery.request, quote: creditRecovery.quote, results, kind: creditRecovery.kind });
}

export function dismissCreditRecovery() {
  recoverySequence += 1;
  creditRecovery.show = false;
  creditRecovery.awaitingBilling = false;
}

function waitForBilling() {
  creditRecovery.show = false;
  creditRecovery.awaitingBilling = true;
  creditRecovery.refreshed = false;
}

export function recoverWithSpark() {
  const view = getCurrentRecoveryView();
  if (['trial-limit', 'fair-use', 'billing-recovery', 'entitlement'].includes(view.failure)) creditRecovery.billingMode = 'tokens';
  waitForBilling();
  openPurchaseModal({ currency: view.premiumOnly || creditRecovery.billingMode === 'tokens' ? 'Premium Spark' : 'Spark',
    required: view.details.required, available: view.details.available });
}

export function recoverWithPlan() {
  creditRecovery.billingMode = 'auto';
  waitForBilling();
  openSubscriptionModal();
}

export async function recoverWithProvider() {
  const sequence = recoverySequence;
  creditRecovery.billingMode = 'auto';
  await manageSubscription(auth.subscription);
  if (sequence !== recoverySequence || accountId() !== creditRecovery.accountId) return;
  if (getCurrentSubscriptionCheckoutUrl()) {
    creditRecovery.awaitingBilling = true;
    creditRecovery.refreshed = false;
  }
  else creditRecovery.actionError = billing.subscriptionError || 'Manage this plan through its billing provider or contact Sogni support.';
}

export async function refreshRecoveryAccount() {
  if (creditRecovery.refreshing) return false;
  const sequence = recoverySequence;
  creditRecovery.refreshing = true;
  creditRecovery.actionError = null;
  try {
    await refreshBillingAccount();
    if (sequence !== recoverySequence || auth.username !== creditRecovery.username) return false;
    markRefreshed(creditRecovery.accountId);
    return true;
  } catch (error) {
    if (sequence === recoverySequence) creditRecovery.actionError = error.message || 'Could not refresh your account. Try again before resuming.';
    return false;
  } finally {
    if (sequence === recoverySequence) creditRecovery.refreshing = false;
  }
}

export function hasRecoveryResumeHandler() {
  return Boolean(activeResume || resumeHandlers.get(creditRecovery.kind));
}

/** A click refreshes identity, balances and entitlement, then delegates to the
 * original UI owner for a fresh quote/approval. Socket updates never call it. */
export async function resumeRecoveredGeneration(options = {}) {
  if (creditRecovery.resuming) return;
  const handler = activeResume || resumeHandlers.get(creditRecovery.kind);
  const promptTokens = !handler && creditRecovery.kind === 'prompt'
    && (options.billingMode || creditRecovery.billingMode) === 'tokens' && creditRecovery.request?.requests?.length;
  if (!handler && !promptTokens) { dismissCreditRecovery(); return; }
  const sequence = recoverySequence;
  creditRecovery.resuming = true;
  if (!await refreshRecoveryAccount() || sequence !== recoverySequence) {
    if (sequence === recoverySequence) creditRecovery.resuming = false;
    return;
  }
  const view = getCurrentRecoveryView();
  const context = { kind: creditRecovery.kind, request: creditRecovery.request, quote: creditRecovery.quote,
    results: creditRecovery.kind === 'images' && creditRecovery.request?.id === generationRun.request?.id
      ? generationRun.results : creditRecovery.results,
    variant: 'original', billingMode: creditRecovery.billingMode, subscription: auth.subscription, ...options };
  if ((context.kind === 'images' && !view.slots.length) || (context.kind === 'video' && !context.request?.items?.length)) {
    creditRecovery.actionError = 'No failed images are ready to retry. Completed and unresolved jobs remain untouched.';
    creditRecovery.resuming = false;
    return;
  }
  creditRecovery.show = false;
  try {
    if (promptTokens) {
      choosePromptTokenRecovery(context.request, accountId());
      creditRecovery.resuming = false;
      dismissCreditRecovery();
    } else await handler(context);
    if (sequence === recoverySequence) creditRecovery.awaitingBilling = false;
  } catch (error) {
    if (error.code !== 'COST_APPROVAL_CANCELED' && sequence === recoverySequence) {
      creditRecovery.actionError = error.message;
      creditRecovery.show = true;
    }
  } finally {
    if (sequence === recoverySequence) creditRecovery.resuming = false;
  }
}
